import { useEffect, useId, useRef, type ReactNode } from 'react';

/**
 * חלון אישור/תוכן. בנייד נפתח מלמטה (נוח לאגודל), במחשב באמצע המסך.
 * כפתור X, Esc ולחיצה על הרקע סוגרים; הפוקוס עובר לחלון כשהוא נפתח ונשאר בתוכו.
 */
export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  // onClose נשמר ב-ref: הורים מעבירים לעתים פונקציה חדשה בכל רינדור,
  // ואסור שזה יפעיל מחדש את האפקט (שמעביר פוקוס לחלון ו"גונב" אותו משדה ההקלדה).
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
      if (e.key === 'Tab' && panel.current) {
        const items = panel.current.querySelectorAll<HTMLElement>('button, a[href], input, textarea, select, [tabindex]:not([tabindex="-1"])');
        if (!items.length) return;
        const first = items[0]!;
        const last = items[items.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center md:items-center">
      <div className="absolute inset-0 bg-ink/50" onClick={() => closeRef.current()} aria-hidden="true" />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="pb-safe relative max-h-[90dvh] w-full overflow-y-auto rounded-t-[18px] bg-surface p-5 outline-none md:max-w-lg md:rounded-card md:p-6"
      >
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-line-strong md:hidden" aria-hidden="true" />
        <div className="flex items-start justify-between gap-3">
          <h2 id={titleId} className="pt-1.5 text-[19px] font-bold">
            {title}
          </h2>
          <button
            type="button"
            onClick={() => closeRef.current()}
            aria-label="סגירה"
            className="-me-2 -mt-1 flex size-11 shrink-0 items-center justify-center rounded-full text-ink-2 hover:bg-sunken hover:text-ink"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
        <div className="mt-3 text-[15px] text-ink-2">{children}</div>
        {footer && <div className="mt-6 flex flex-col-reverse gap-3 md:flex-row md:justify-start">{footer}</div>}
      </div>
    </div>
  );
}
